import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { LoginService } from '../login/services/login-service.service';
import { ChoiceListResponse } from './choice-list/choice-list-response.dto';
import { Document, Alert } from './document/document.dto';
import { Folder } from './folder/folder.dto';
import { ObjectStoreResponse } from './object-store/object-store-response.dto';
import { environment } from 'src/environments/environment';
import { mapPropertiesObj } from './general/generic.function';
import { SearchResultDTO } from '../acl-search/search-result.dto';

const serviceURL = '/i1FilenetServices2/v1/FilenetServices';
const serviceURL_ACL = environment.serviceURL_ACL;
const reportsURL = environment.reportRoot;
const usersGroup = environment.usersGroup;

@Injectable()
export class I1FilenetServices {
    constructor(
        private httpClient: HttpClient,
        private loginService: LoginService
    ) {}

    public getObjectStores(): Observable<ObjectStoreResponse> {
        return this.httpClient.get<ObjectStoreResponse>(
            `${serviceURL_ACL}/getObjectStores`
        );
    }

    public getFolders(
        objectStore: string,
        folderIds: string[],
        completeValue = 'false'
    ) {
        return this.httpClient.post<Folder[]>(
            `${serviceURL_ACL}/getACLFolders/${objectStore}`,
            folderIds,
            { params: { complete: completeValue } }
        );
    }

    public getDuplicateDocument(
        objectStore: string,
        hash: string,
        fileSize: string,
        DocumentTitle: string,
        path: string
    ): Observable<any[]> {
        let myParams = new HttpParams();

        // Begin assigning parameters
        myParams = myParams.append('objectStoreName', objectStore);
        myParams = myParams.append('fileHash', hash);
        myParams = myParams.append('fileSize', fileSize.toString());
        myParams = myParams.append('documentTitle', DocumentTitle);
        myParams = myParams.append('path', path);
        return this.httpClient.get<any>(
            `${serviceURL_ACL}/getDuplicateDocument`,
            {
                params: myParams,
            }
        );
    }

    public updateDocument(objectStore: string, document: Document) {
        // Begin assigning parameters
        return this.httpClient.post<Document>(
            `${serviceURL_ACL}/updateContract/${objectStore}`,
            document
        );
    }
    
    public bulkUpdateDocuments(objectStore: string, documents: Document[]) {
        return this.httpClient.post<Document[]>(
            `${serviceURL_ACL}/updateContracts/${objectStore}`,
            documents
        );
    }

    public addComment(
        objectStore: string,
        documentID: string,
        comment: string
    ) {
        // Begin assigning parameters
        return this.httpClient.post<Alert>(
            `${serviceURL_ACL}/addComment/${objectStore}/${documentID}`,
            comment
        );
    }

    public searchContract(objectStore: string, input: any) {
        // Begin assigning parameters
        const queryObj: any = mapPropertiesObj({ propertiesObj: input });
        queryObj.searchResultLimit = environment.limitSearch;
        console.log('Query', queryObj);
        return this.httpClient.post<SearchResultDTO[]>(
            `${serviceURL_ACL}/searchContract/${objectStore}`,
            queryObj,
            { observe: 'response' }
        );
    }

    public downloadSearchContract(objectStore: string, input: any) {
        // Begin assigning parameters
        // console.log('Input', input);
        const limit = input.searchResultsLimit;

        const queryObj: any = mapPropertiesObj({ propertiesObj: input });
        queryObj.searchResultLimit = limit;
        console.log('Query', queryObj);
        return this.httpClient.post(
            `${reportsURL}downloadSearchReport/${objectStore}`,
            queryObj,
            {
                responseType: 'arraybuffer',
                headers: {
                    Accept:
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                },
            }
        );
    }

    public updateAlert(objectStore: string, alert: Alert) {
        // Begin assigning parameters
        return this.httpClient.post<Alert>(
            `${serviceURL_ACL}/updateAlert/${objectStore}`,
            alert
        );
    }

    public updateFolder(objectStore: string, folder: Folder) {
        // Begin assigning parameters
        return this.httpClient.post<Folder[]>(
            `${serviceURL_ACL}/updateFolder/${objectStore}`,
            folder
        );
    }

    //Below is hard coded values, do not use
    // public updateFolders(folders){
    //     return this.httpClient.post<Folder[]>(`${serviceURL_ACL}/updateFolders/ACL2`,folders);
    // }

    public getDocuments(
        objectStore: string,
        documentIds: string[],
        completeValue: string,
        searchResultsValue: string
    ) {
        let myParams = new HttpParams();

        // Begin assigning parameters
        myParams = myParams.append('complete', completeValue);
        myParams = myParams.append('searchResults', searchResultsValue);
        return this.httpClient.post<Document[]>(
            `${serviceURL_ACL}/getACLDocuments/${objectStore}`,
            documentIds,
            {
                params: myParams,
            }
        );
    }

    public getAllUsers() {
        return this.httpClient.post<any[]>(
            `${environment.userManagementURL}getAllUsers`,
            null
        );
    }

    public getMembers(input: string) {
        return this.httpClient.post<any[]>(
            `${environment.userManagementURL}group/${input}`,
            null
        );
    }
    public addMember(username: string, group: string) {
        return this.httpClient.put<any[]>(
            `${environment.userManagementURL}${username}/addToGroup/${group}`,
            null
        );
    }

    public removeMember(username: string, group: string) {
        return this.httpClient.put<any[]>(
            `${environment.userManagementURL}${username}/removeFromGroup/${group}`,
            null
        );


    }

    public deleteMember(username: string, objectStore: string) {

        // console.log("##################",objectStore)
        return this.httpClient.post<any[]>(
            `${environment.deleteUserURL}/${objectStore}/${username}`, null

        );
    }

    public getUsers() {
        return this.httpClient.get<string[]>(
            `${serviceURL_ACL}/getUsers/${usersGroup}`
        );
    }

    public getAuditUsers() {
        return this.httpClient.get<string[]>(`${serviceURL_ACL}/getAuditUsers`);
    }

    public recordViewAudit(objectStore: string, id: string, title: string) {
        return this.httpClient.post<any>(
            `${serviceURL_ACL}/recordViewAudit/${objectStore}/${id}/${title}`,
            null
        );
    }

    public getChoiceListEntries(
        objectStore: string,
        name: string
    ): Observable<any> {
        let myParams = new HttpParams();

        // Begin assigning parameters
        myParams = myParams.append('objectStoreName', objectStore);
        myParams = myParams.append('choiceListName', name);

        return this.httpClient
            .get<ChoiceListResponse>(`${serviceURL}/getChoiceListEntries`, {
                params: myParams,
            })
            .pipe(
                map(value =>
                    value.data.reduce((obj, val) => {
                        // console.log('val', val);
                        let index;
                        index = Number(val.value);
                        if (!index) {
                            index = val.value;
                        }

                        obj[val.value] = val.description;
                        return obj;
                    }, {})
                )
            );
    }

    public getFolderNames(objectStore: string, name: string): Observable<any> {
        return this.httpClient
            .get<string[]>(
                `${serviceURL_ACL}/getFolderNames/${objectStore}/${name}`
            )
            .pipe(
                map(value =>
                    value.reduce((obj, val) => {
                        obj[val] = val;
                        return obj;
                    }, {})
                )
            );
    }
}
